import { useEffect, useState } from "react";
import { calculateTimeLeft } from "../utils/timeHelpers";

const THREE_HOURS_IN_SECONDS = 3 * 60 * 60;

const STORAGE_KEY = "otp_resend_timer";

interface StoredTimer {
  resendCodeCount: number;
  resendDate: number;
  expireAt: number;
}

const toFiniteNumber = (value: unknown): number => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value !== "string") {
    return Number.NaN;
  }

  const normalizedValue = value
    .replace(/[٠-٩]/g, (digit) =>
      String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)),
    )
    .replace(/[۰-۹]/g, (digit) =>
      String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)),
    )
    .replace(/٬/g, "")
    .replace(/٫/g, ".");

  return Number(normalizedValue);
};

const useResendTimer = (resendData: any) => {
  const [resendCodeCount, setResendCodeCount] = useState(0);
  const [resendDate, setResendDate] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  /*
   * Get timer from localStorage
   */
  const getStoredTimer = (): StoredTimer | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        return null;
      }

      const parsed: StoredTimer = JSON.parse(stored);

      if (
        !parsed ||
        !Number.isFinite(parsed.resendCodeCount) ||
        !Number.isFinite(parsed.resendDate) ||
        !Number.isFinite(parsed.expireAt)
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  };

  /*
   * Save timer
   */
  const saveTimer = (count: number, date: number, expireAt: number) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          resendCodeCount: count,
          resendDate: date,
          expireAt,
        }),
      );
    } catch {
      // Ignore localStorage errors
    }
  };

  /*
   * API data
   */
  useEffect(() => {
    /*
     * IMPORTANT:
     *
     * During language change React Query can temporarily
     * make resendData undefined.
     *
     * DO NOT reset the timer here.
     */
    if (!resendData) {
      return;
    }

    const data = resendData.Data ?? resendData;

    const count = toFiniteNumber(data?.ResendCodeCount);
    let date = toFiniteNumber(data?.ResendDate);

    if (!Number.isFinite(count) || !Number.isFinite(date) || date <= 0) {
      return;
    }

    const now = Date.now() / 1000;

    /*
     * Your backend timestamp is approximately
     * 3 hours ahead.
     */
    if (date > now + 5 * 60) {
      date -= THREE_HOURS_IN_SECONDS;
    }

    /*
     * Wait time based on count.
     *
     * We don't modify calculateTimeLeft.
     * We only calculate the expiry date here.
     */
    const waitTimes: Record<number, number> = {
      1: 2 * 60,
      2: 5 * 60,
      3: 30 * 60,
    };

    const waitTime = waitTimes[count] ?? 0;

    if (waitTime <= 0) {
      return;
    }

    const expireAt = date + waitTime;

    /*
     * If the API gives us the same timer again,
     * don't restart it.
     *
     * This is important after language switching.
     */
    const existing = getStoredTimer();

    if (
      existing &&
      existing.resendCodeCount === count &&
      Math.abs(existing.resendDate - date) < 2 &&
      existing.expireAt > now
    ) {
      setResendCodeCount(existing.resendCodeCount);

      setResendDate(existing.resendDate);

      setTimeLeft(Math.max(Math.ceil(existing.expireAt - now), 0));

      return;
    }

    /*
     * New timer from API
     */
    saveTimer(count, date, expireAt);

    setResendCodeCount(count);
    setResendDate(date);

    const calculatedTime = calculateTimeLeft(count, date);

    /*
     * If calculateTimeLeft gives a valid value,
     * use it.
     */
    setTimeLeft(Math.max(Math.ceil(calculatedTime), 0));
  }, [resendData]);

  /*
   * Restore timer from localStorage on mount
   *
   * This handles:
   *
   * - page refresh
   * - language change
   * - component remount
   */
  useEffect(() => {
    const stored = getStoredTimer();

    if (!stored) {
      return;
    }

    const now = Date.now() / 1000;

    const remaining = Math.max(Math.ceil(stored.expireAt - now), 0);

    if (remaining <= 0) {
      localStorage.removeItem(STORAGE_KEY);

      setTimeLeft(0);

      return;
    }

    setResendCodeCount(stored.resendCodeCount);

    setResendDate(stored.resendDate);

    setTimeLeft(remaining);
  }, []);

  /*
   * Countdown
   */
  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setTimeLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [timeLeft]);

  /*
   * Remove expired timer
   */
  useEffect(() => {
    if (timeLeft !== 0) {
      return;
    }

    const stored = getStoredTimer();

    if (!stored) {
      return;
    }

    const now = Date.now() / 1000;

    if (stored.expireAt <= now) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [timeLeft]);

  return {
    resendCodeCount,
    resendDate,
    timeLeft,
    setTimeLeft,
  };
};

export default useResendTimer;
